<script setup lang="ts">
import type { EmblaPluginType, EmblaOptionsType } from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import { Carousel } from '@/components/ui/carousel'
import type {PortfolioType} from "~/types/Portfolio";
import {RoleEnum} from "~/types/User";
import {roundMoney} from "~/common/helpers";

const { t, locale } = useI18n();
const router = useRouter()
const Loader = useIsLoadingStore()

const userStore = useUserStore();


const props = defineProps({
    portfolio: {
        type: Object as PropType<PortfolioType>,
        required: true,
    },
    educationTypeList: {
        type: Array,
        default: () => []
    },
    specification_name: {
        type: String,
        default: null
    },
    review_tags: {
        type: Array,
        default: () => []
    },
    reviews: {
        type: Array,
        default: () => []
    },
    reviews_total: {
        type: Number,
        default: 0
    },
    similarSpecialists: {
        type: Array as PropType<PortfolioType[]>,
        default: () => []
    },
})

const emit = defineEmits(['loadMoreReviews', 'changeMode'])

const masterSpecificationIds = computed(() => {
    return props.portfolio.specifications.map(item => item.documentId)
})

const breadcrumbs = [
    {
        label: t('Home'),
        url: '/',
    },
    {
        label: t('Catalog'),
        url: '/catalog',
    },
];

function extractYouTubeVideoID(url: string): string | null {
    const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
}

const carouselContainerRef = ref<InstanceType<typeof Carousel> | null>(null) // TODO: Поправить слайдер в арабской версии https://stackblitz.com/edit/github-gmh6db-bjqkxt?file=app.vue

const key = ref(1)
const direction = computed(() => locale.value === 'ar' ? 'rtl' : 'ltr')

const plugins = ref<EmblaPluginType[]>([])
const autoplay = Autoplay({
    delay: 2000,
    stopOnMouseEnter: true,
    stopOnInteraction: false,
})
plugins.value.push(autoplay)

const carousel_options = ref<EmblaOptionsType>({
    align: 'start',
    //loop: false,
    skipSnaps: true,
    direction: direction.value
})

watch(locale, async (newLocale) => {
    carousel_options.value.direction = direction.value
    await nextTick(() => {
        carouselContainerRef.value?.carouselApi?.reInit(carousel_options.value)
        autoplay.reset()
    })
});

const zoom = ref(12)
const center = ref({lat: 43.238131, lng: 76.942795})
const userLocation = ref<any>(null);
const map = ref(null)

function locateUser() {
    map.value?.leafletObject?.locate()
}

function onMapReady() {
    locateUser()
}

function onLocationFound(location: any) {
    userLocation.value = location.latlng
    center.value = location.latlng
    map.value?.leafletObject?.panTo(location.latlng)
}

const avgDistance = computed(() => {
    if(!props.portfolio?.geo?.lat || !props.portfolio?.geo?.lng || !userLocation.value?.lat || !userLocation.value?.lng) return null
    const R = 6371
    const dLat = (props.portfolio.geo.lat - userLocation.value.lat) * (Math.PI/180)
    const dLng = (props.portfolio.geo.lng - userLocation.value.lng) * (Math.PI/180)

    const a =
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(userLocation.value.lat * (Math.PI/180)) * Math.cos(props.portfolio.geo.lat * (Math.PI/180)) *
        Math.sin(dLng/2) * Math.sin(dLng/2)

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
    return Math.round(R * c * 10) / 10
})

</script>

<template>
    <div class="">
        <div class="mobile_header md:hidden grid grid-cols-6 items-center my-4">
            <div class="col-span-1">
                <UiButton @click.prevent="router.back()" variant="ghost" class="p-0">
                    <Icon name="ri:arrow-left-line" size="24px" />
                </UiButton>
            </div>
            <div class="col-span-4">
                <p class="text-center font-bold uppercase text-[13px] tracking-widest leading-none">{{ $t('Specialists nearby') }}</p>
            </div>
            <div class="col-span-1">
            </div>
        </div>
    <div class="grid gap-12 lg:grid-cols-12 mt-8">
        <div class="aside order-2 lg:order-1 lg:col-span-4 flex flex-col">
            <div class="hidden lg:block page_title mb-8 order-1">
                <div class="flex items-center gap-6">
                    <UiButton @click.prevet="router.back()" class="p-3 h-auto text-black" variant="lighter">
                        <Icon name="ri:arrow-left-line" size="24px"/>
                    </UiButton>
                    <p class="font-semibold text-xl">Portfolio</p>
                </div>
            </div>

            <div class="flex gap-4 cstm_style_avatar order-3">
                <CustomUiUserAvatar :src="portfolio.user.avatar?.formats?.small?.url" :name="portfolio.user.username" size="md" class="" shape="square" />

                <div class="h-full">
                    <p class="text-lg font-medium">{{ portfolio.user.username }}</p>
                    <p class="text-gray-500 text-sm">{{ specification_name }}</p>
                    <div v-if="portfolio.user?.compute_review_count" class="flex items-center justify-start mt-2">
                        <Icon class="text-gray-500" size="24" name="ri:chat-heart-line" />
                        <p class="text-sm ltr:ml-1 rtl:mr-1 leading-none"> {{ portfolio.user.compute_review_count }}
                            {{ $t('reviews') }}
                        </p>
                    </div>
                    <div v-if="portfolio.user.compute_rating" class="flex items-center mt-1">
                        <Icon name="ri:star-fill" size="14px"
                              class="text-[#F2AE40] mx-1" />
                        <span class="text-gray-500 text-xs">{{ portfolio.user.compute_rating?.toFixed(1) }}</span>
                    </div>
                </div>
            </div>

            <div class="md:col-span-7 col-span-full order-4">
                <div class="">

                    <div v-if="portfolio.specifications.length"
                         class="flex flex-wrap gap-2 mt-4 w-fit">
                        <CustomUiTagBadge v-for="tag in portfolio.specifications" variant="specification">{{ tag.name }}
                        </CustomUiTagBadge>
                    </div>

                    <p v-if="portfolio.description" class="mt-4 font-medium text-sm">
                        {{ portfolio.description }}
                    </p>

                    <div v-if="portfolio.work_experience" class="mt-4 md:mt-3">
                        <p class="mb-2 text-sm font-medium">{{ $t("Work experience") }}</p>
                        <CustomUiTagBadge>{{ t('Over {years} years', { years: portfolio.work_experience }) }}
                        </CustomUiTagBadge>
                    </div>

                    <div v-if="portfolio.education" class="mt-4 md:mt-3">
                        <p class="mb-2 text-sm font-medium">{{ $t("Education") }}</p>
                        <CustomUiTagBadge>{{ portfolio.education.name }}
                        </CustomUiTagBadge>
                    </div>

                    <div v-if="portfolio?.price" class="mt-4 md:mt-3">
                        <p class="mb-2 text-sm font-medium">{{ $t("Prices") }}</p>
                        <CustomUiTagBadge v-if="portfolio?.price">
                            <span v-if="portfolio.price?.from && !portfolio.price?.to">
                                {{ $t("from") }} {{ roundMoney(portfolio.price.from) }} AED
                            </span>
                            <span v-else-if="portfolio.price?.to && !portfolio.price?.from">
                                {{ $t("up to") }} {{ roundMoney(portfolio.price.to) }} AED
                            </span>
                            <span v-else>
                                {{ roundMoney(portfolio.price?.from) }}-{{ roundMoney(portfolio.price?.to) }} AED
                            </span>
                        </CustomUiTagBadge>
                    </div>

                    <div class="mt-4" v-if="portfolio.user.languages?.length">
                        <p class="mb-1 text-sm font-medium">{{ $t("Speaking in") }}</p>
                        <div class="flex flex-wrap gap-1">
                            <p v-for="lang in portfolio.user.languages" class="catalog-portfolio-user-tag w-fit"> {{ lang.name }}</p>
                        </div>
                    </div>

                    <template v-if="portfolio.images?.length || portfolio.video_links?.length">
                        <div class="master__protfolio mt-4">
                            <p class="mb-2 text-sm font-medium">
                                {{ $t("Photos & video") }}
                            </p>
                            <UiCarousel
                                    v-if="portfolio.images"
                                    class="relative w-full"
                                    :opts="{
                                            align: 'start',
                                        }"
                            >
                                <UiCarouselContent  class="-ml-4">
                                    <UiCarouselItem
                                            v-for="(img, index) in portfolio.images"
                                            class="pl-4 basis-[141px] h-[125px]">
                                        <CustomUiPhotoViewer :url="img.formats?.large?.url || img.formats?.medium?.url" :thumbnail="img.formats?.thumbnail?.url" class="aspect-square relative overflow-hidden rounded-xl" />
                                    </UiCarouselItem>

                                    <UiCarouselItem
                                            v-for="(video, index) in portfolio.video_links"
                                            class="pl-4 basis-[238px] h-[125px]"
                                    >
                                        <UiDialog>
                                            <UiDialogTrigger>
                                                <div class="aspect-video relative overflow-hidden rounded-xl">
                                                    <img
                                                            :src="`https://img.youtube.com/vi/${extractYouTubeVideoID(video.url)}/maxresdefault.jpg`"
                                                            class="object-cover w-full h-full"
                                                            :alt="'Video thumbnail ' + (index + 1)"
                                                    />
                                                    <div class="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-white/70 transition-colors">
                                                        <Icon
                                                                name="ri:youtube-fill"
                                                                class="w-10 h-10 text-white"
                                                        />
                                                    </div>
                                                </div>
                                            </UiDialogTrigger>

                                            <UiDialogContent class="sm:max-w-[90vw] sm:max-h-[80vh]">
                                                <div class="aspect-video w-full">
                                                    <iframe
                                                            class="w-full h-full"
                                                            :src="`https://www.youtube.com/embed/${extractYouTubeVideoID(video.url)}?autoplay=1`"
                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                            allowfullscreen
                                                    />
                                                </div>
                                            </UiDialogContent>
                                        </UiDialog>
                                    </UiCarouselItem>
                                </UiCarouselContent>
                            </UiCarousel>
                        </div>
                    </template>
                </div>


                <div v-if="reviews.meta?.total" class="mt-8">
                    <p class="description-title">
                        {{ $t("Reviews") }}
                    </p>

                    <template v-if="review_tags.length || reviews.length">
                        <div v-if="review_tags.length" class="mt-4">
                            <p class="mb-2 text-sm font-medium">{{ $t("Feedback from client") }}</p>
                            <div class="flex flex-wrap gap-2 w-fit">
                                <CustomUiTagBadge v-for="tag in review_tags">{{ tag.name }}</CustomUiTagBadge>
                            </div>
                        </div>

                        <div v-if="reviews.meta?.total" class="mt-4">
                            <p class="mb-2 text-sm font-medium">{{ $t("Recommended Reviews") }}</p>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div v-for="review in reviews.data"
                                     class="border border-gray-200 p-4 flex flex-col justify-between rounded-lg">
                                    <div class="top_row">
                                        <p v-if="review.owner?.username" class="text-sm">{{ review.owner.username }}</p>
                                        <p v-if="review.text" class="mt-2 text-sm break-words">{{ review.text }}</p>
                                    </div>
                                    <div class="bottom_row flex items-end justify-between mt-2">
                                        <CustomUiRatingStars :rating="review.rating" />
                                        <p class="text-[10px]">{{ $formatDateTime(review.createdAt, 'MMMM dd, yyyy') }}</p>
                                    </div>
                                </div>
                            </div>
                            <UiButton v-if="reviews.meta?.total > reviews.length" :disabled="Loader.isLoading" @click.prevent="emit('loadMoreReviews')"
                                      variant="outline"
                                      class="text-sm px-7 py-2.5 mt-2 rounded-lg border-primary-500 !text-primary-500 hover:bg-primary-50 hover:border-primary-50  transition-all">
                                <Icon v-if="Loader.isLoading" name="svg-spinners:ring-resize" />
                                <span v-else>{{ $t("Load more") }}</span>
                            </UiButton>
                        </div>
                    </template>

                    <div v-else class="mt-4">
                        <p>{{ $t('No reviews yet') }}</p>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-12 gap-4 lg:gap-8 mb-8 lg:mt-8 lg:mb-0 order-2 lg:order-7">
                <UiButton variant="lighter" class="col-span-6 lg:col-span-5 py-4 h-auto text-black" as-child>
                    <NuxtLink :to="{name: 'master_category', params: {slug: portfolio.specifications[0].slug, city: userStore.currentCity?.slug}}">
                        {{$t('All specialists')}}
                    </NuxtLink>
                </UiButton>
                <template v-if="userStore.currentRole != RoleEnum.MASTER">
                    <PagesCatalogSuggestOrder
                        v-if="userStore.isAuthenticated"
                        :master_id="portfolio.user.documentId"
                        :specification_id="portfolio.specifications[0].parent_id"
                        :onlySpecificationIds="masterSpecificationIds"
                        btn_class="w-full py-4 h-auto col-span-6 lg:col-span-7"
                    />
                    <LayoutsAuthMainElement
                        v-else
                        :icon="false"
                        :title="$t('Suggest order')"
                        className="w-full rounded-lg bg-primary-500 hover:bg-primary-600 col-span-6 lg:col-span-7 py-4 h-auto" />
                </template>
            </div>

        </div>

        <div class="order-1 lg:order-2 col-span-1 lg:col-span-8">
            <client-only>
                <div>
                    <LMap
                    ref="map"
                    :center="center"
                    :options="{
                        attributionControl: false,
                        zoomControl: false,
                    }"
                    :use-global-leaflet="false"
                    :zoom="zoom"
                    class="rounded-2xl min-h-[400px] lg:min-h-[800px] z-0"
                    @locationfound="onLocationFound"
                    @ready="onMapReady"
                >
                    <LTileLayer
                        attribution="&amp;copy; <a href=&quot;https://www.openstreetmap.org/&quot;>OpenStreetMap</a> contributors"
                        layer-type="base"
                        name="OpenStreetMap"
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <LMarker v-if="userLocation" :lat-lng="userLocation" color="red">
                        <LIcon :icon-size="[60, 60]"
                               class-name="relative overflow-visible flex items-center justify-center">
                            <div
                                class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-primary-400"></div>
                            <div
                                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white border-[6px] border-primary-400"></div>
                        </LIcon>
                    </LMarker>

                    <LMarker v-if="portfolio.geo" :lat-lng="[portfolio.geo.lat, portfolio.geo.lng]" color="red">
                        <LIcon :icon-size="[48, 48]" class-name="">
                            <CustomUiUserAvatar
                                :name="portfolio.user.username"
                                :src="portfolio.user.avatar?.formats?.small?.url"
                                class="border-2 border-white shadow"
                                size="xs"/>
                        </LIcon>
                    </LMarker>

                    <LControlZoom position="bottomright"/>
                    <LControl position="bottomright">
                        <button
                            class="leaflet_location_btn w-12 h-12 mb-4 rounded-full leading-[48px] bg-white hover:bg-gray-100 focus:outline-none"
                            @click="locateUser"
                        >
                            <Icon
                                :class="{
                                        'text-gray-400': !userLocation,
                                        'text-primary-400': !!userLocation,
                                    }"
                                name="ri:send-plane-fill"
                                size="18px"/>
                        </button>
                    </LControl>
                    <LControl position="topleft">
                        <UiTabs
                            @update:modelValue="(mode: string) => navigateTo({name: 'portfolio', params: {id: portfolio.documentId}, query: {mode: mode}})"
                            default-value="geo"
                            class="bg-white rounded-xl"
                        >
                            <UiTabsList>
                                <UiTabsTrigger value="geo" class="rounded-xl py-3 data-[state=active]:bg-primary-500 data-[state=active]:text-white">
                                    <Icon name="ri:map-2-line" size="24" />
                                </UiTabsTrigger>
                                <UiTabsTrigger value="list" class="rounded-xl py-3 data-[state=active]:bg-primary-500 data-[state=active]:text-white" >
                                    <Icon name="ri:list-check-2" size="24" />
                                </UiTabsTrigger>
                            </UiTabsList>
                        </UiTabs>
                    </LControl>
                    
                    <LControl v-if="avgDistance" position="topright">
                        <div class="px-7 py-1 5 rounded-xl shadow-md bg-white text-gray-400 text-center">
                            <p class="font-bold text-[16px]"><span class="text-main">{{avgDistance}}</span> {{$t('km')}}</p>
                            <p class="text-xs">{{$t('Avg distance')}}</p>
                        </div>
                    </LControl>
                </LMap>
                </div>
            </client-only>
        </div>

    </div>
    </div>
</template>

<style scoped lang="scss">
.catalog-portfolio-user-tag {
    height: 24px;
    padding: 4px 8px 4px 8px;
    gap: 3px;
    border-radius: 6px;
    border: 1px solid rgba(226, 228, 233, 1);
    background: rgba(255, 255, 255, 1);
    color: rgba(10, 13, 20, 1);
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    text-align: center;
}

.description-title {
    font-family: Involve, sans-serif;
    font-size: 26px;
    font-weight: 500;
    line-height: 34.58px;
    text-align: start;
    @media(max-width: 768px){
        font-size: 18px;
        line-height: 24px;
    }
}
</style>